import { IdentityInfo, DnsRecord, getAccountAlias, getAccountEnvironment } from '@/lib/types'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Copy, Check, Download } from 'lucide-react'
import { useState } from 'react'

interface DomainCardProps {
  domain: IdentityInfo
}

const statusColors: Record<string, string> = {
  SUCCESS: 'bg-teal-500/15 text-teal-700 hover:bg-teal-500/25',
  PENDING: 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25',
  FAILED: 'bg-rose-500/15 text-rose-700 hover:bg-rose-500/25',
  TEMPORARY_FAILURE: 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25',
  NOT_STARTED: 'bg-zinc-500/15 text-zinc-700 hover:bg-zinc-500/25',
}

const dkimStatusColors: Record<string, string> = {
  SUCCESS: 'bg-teal-500/15 text-teal-700 hover:bg-teal-500/25',
  PENDING: 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25',
  FAILED: 'bg-rose-500/15 text-rose-700 hover:bg-rose-500/25',
  TEMPORARY_FAILURE: 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25',
  NOT_STARTED: 'bg-zinc-500/15 text-zinc-700 hover:bg-zinc-500/25',
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </Button>
  )
}

function escapeCsvField(field: string): string {
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return `"${field.replace(/"/g, '""')}"`
  }
  return field
}

export function DomainCard({ domain }: DomainCardProps) {
  const downloadDnsRecordsCsv = () => {
    if (!domain.dns_records || domain.dns_records.length === 0) return

    const headers = ['Type', 'Name', 'Priority', 'Value']
    const rows = domain.dns_records.map((record) => [
      escapeCsvField(record.type),
      escapeCsvField(record.name),
      record.type === 'MX' && record.priority !== undefined ? String(record.priority) : '',
      escapeCsvField(record.content),
    ])

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${domain.domain}-dns-records.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      {/* Domain Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{domain.domain}</CardTitle>
          <CardDescription>Domain information and status</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Account</dt>
              <dd className="mt-1">
                <Badge
                  variant="outline"
                  className={
                    getAccountEnvironment(domain.account_id) === 'prod'
                      ? 'bg-emerald-500/15 text-emerald-700 border-emerald-300'
                      : getAccountEnvironment(domain.account_id) === 'staging'
                      ? 'bg-amber-500/15 text-amber-700 border-amber-300'
                      : ''
                  }
                >
                  {getAccountAlias(domain.account_id)}
                </Badge>
                <span className="ml-2 font-mono text-sm text-muted-foreground">{domain.account_id}</span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Configuration Set</dt>
              <dd className="mt-1 text-sm">{domain.configuration_set_name || 'None'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Verification Status</dt>
              <dd className="mt-1">
                <Badge className={statusColors[domain.verification_status] || ''}>
                  {domain.verification_status || '-'}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">DKIM Status</dt>
              <dd className="mt-1">
                <Badge className={dkimStatusColors[domain.dkim_status] || ''}>
                  {domain.dkim_status || '-'}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="mt-1 text-sm">{formatDate(domain.created_at)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Last Updated</dt>
              <dd className="mt-1 text-sm">{formatDate(domain.updated_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* DNS Records Card */}
      {domain.dns_records && domain.dns_records.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>DNS Records</CardTitle>
              <CardDescription>
                Configure these DNS records in your domain registrar
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={downloadDnsRecordsCsv}>
              <Download className="mr-2 h-4 w-4" />
              Download CSV
            </Button>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Type</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-20">Priority</TableHead>
                    <TableHead>Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {domain.dns_records.map((record: DnsRecord, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Badge variant="outline">{record.type}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-xs break-all max-w-[200px]">
                            {record.name}
                          </span>
                          <CopyButton text={record.name} />
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {record.type === 'MX' && record.priority !== undefined ? record.priority : ''}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-xs break-all max-w-[300px]">
                            {record.content}
                          </span>
                          <CopyButton text={record.content} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
